'use client';

import { type ComponentProps, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { formatLocaleDate } from '@kit/shared/utils';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import { Card, CardContent, CardHeader } from '@kit/ui/card';
import { CurrencyIcon } from '@kit/ui/currency-icon';
import { cn } from '@kit/ui/utils';

import { DetailsDrawer } from '~/(tenant)/_components/details-drawer';
import { useNativeLayout } from '~/(tenant)/_components/native/_lib/native-layout-context';
import { NativeFilterChip } from '~/(tenant)/_components/native/_primitives/native-filter-chip';

import {
  PACKAGE_DESCRIPTION_OVERFLOW_CHARS,
  PACKAGE_FEATURES_PREVIEW_COUNT,
  formatPackageLabel,
  selectPackageCopy,
} from '../_lib/format-package-label';
import {
  type PublicDiscountCode,
  publicCodesForPackage,
} from '../_lib/public-discount-codes';
import type { BookingPackage } from '../_lib/server/booking-page.loader';
import { useBookingPresentation } from './booking-presentation';
import styles from './native-booking-controls.module.css';
import { PackageOffersBadge, PackageOffersDrawer } from './package-offers';

function PackageFilterButton({
  variant,
  size,
  ...props
}: ComponentProps<typeof Button>) {
  const isNative = useNativeLayout();
  if (isNative)
    return (
      <NativeFilterChip {...props} active={props['aria-pressed'] === true} />
    );
  return <Button {...props} variant={variant} size={size} />;
}

interface PackageSubscriberCount {
  current_subscribers: number;
  max_subscribers: number | null;
  remaining_slots: number | null;
}

interface PackageSelectionProps {
  packages: BookingPackage[];
  onPackageSelect: (packageData: {
    id: string;
    name: string;
    price: number;
    booking_count: number;
    slot_duration?: number;
    validity_months?: number;
    validity_days?: number | null;
    original_price?: number | null;
    package_type?: string;
    player_count?: number;
    player_pricing?: Record<string, number> | null;
    description?: string | null;
    features?: string[];
    color?: string | null;
    max_subscribers?: number | null;
    current_subscribers?: number;
    remaining_slots?: number | null;
    is_bundle?: boolean;
    is_fixed_slot?: boolean;
    location_id?: string | null;
    require_attendance_within_validity?: boolean;
    fixed_starts_at?: string | null;
    fixed_ends_at?: string | null;
  }) => void;
  selectedPackageId?: string;
  /** Tenant-advertised discount codes; each card shows an offers badge with
   *  the codes that apply to it. */
  publicDiscountCodes?: PublicDiscountCode[];
}

const PACKAGE_COLORS = {
  gold: '#B8860B',
  silver: '#C0C0C0',
  bronze: '#CD7F32',
};

export function PackageSelection({
  packages: allPackages,
  onPackageSelect,
  selectedPackageId,
  publicDiscountCodes = [],
}: PackageSelectionProps) {
  const presentation = useBookingPresentation();
  const supabase = useSupabase();
  const { t, i18n } = useTranslation('bookings');
  const [selectedPackageType, setSelectedPackageType] = useState<string>('all');
  const [detailsPackageId, setDetailsPackageId] = useState<string | null>(null);
  const [offersPackageId, setOffersPackageId] = useState<string | null>(null);

  // Public offers per package — display-level narrowing only; redemption is
  // re-validated server-side at checkout.
  const offersForPackage = (pkg: BookingPackage) =>
    publicCodesForPackage(publicDiscountCodes, pkg);

  // Derive unique package types from actual data
  const uniquePackageTypes = [
    ...new Set(allPackages.map((pkg) => pkg.package_type)),
  ];

  // Package type labels using i18n keys (respects business-type overrides)
  const packageTypeLabels: Record<string, string> = {
    court: t('courtOnly'),
    trainer: t('coaching'),
    kids_academy: t('kidsAcademy'),
    group_classes: t('groupClasses'),
  };

  // Build dynamic package type options from actual data
  const packageTypes = [
    { value: 'all', label: t('filterAll') },
    ...uniquePackageTypes.map((type) => ({
      value: type,
      label:
        packageTypeLabels[type] ||
        type
          .split('_')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' '),
    })),
  ];

  // Only show filter if there are multiple package types
  const showPackageTypeFilter = uniquePackageTypes.length > 1;

  // Filter packages client-side based on selected type
  const packages = allPackages.filter(
    (pkg) =>
      selectedPackageType === 'all' || pkg.package_type === selectedPackageType,
  );

  // Fetch subscriber counts for packages with limits
  const { data: subscriberCounts } = useQuery({
    queryKey: ['package-subscriber-counts', packages?.map((p) => p.id)],
    queryFn: async () => {
      const counts: Record<string, PackageSubscriberCount> = {};

      // Only fetch counts for packages with max_subscribers set
      const packagesWithLimits = packages.filter(
        (p) => p.max_subscribers !== null,
      );

      await Promise.all(
        packagesWithLimits.map(async (pkg) => {
          const { data, error } = await supabase.rpc(
            'get_package_subscriber_count',
            {
              p_package_id: pkg.id,
            },
          );

          if (!error && data && data.length > 0 && data[0]) {
            counts[pkg.id] = data[0];
          }
        }),
      );

      return counts;
    },
    enabled: packages.length > 0,
  });

  if (packages.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2 lg:space-y-4">
      <div className="mb-2 flex items-center gap-2 lg:mb-4">
        <h3 className="text-lg font-semibold lg:text-xl">
          {t('premiumPackages')}
        </h3>
      </div>

      {/* Package Type Filter - Only show if multiple package types exist */}
      {showPackageTypeFilter && (
        <>
          {/* Mobile (Horizontal Scroll) */}
          <div
            className={cn(
              'flex snap-x snap-mandatory gap-1.5 overflow-x-auto pb-2 lg:hidden',
              styles.filterTrack,
            )}
          >
            {packageTypes.map((type) => (
              <PackageFilterButton
                key={type.value}
                aria-pressed={selectedPackageType === type.value}
                variant={
                  selectedPackageType === type.value ? 'default' : 'outline'
                }
                size="sm"
                onClick={() => setSelectedPackageType(type.value)}
                className={cn(
                  styles.typeOption,
                  styles.filterOption,
                  'h-8 flex-shrink-0 snap-start px-3 py-1.5 text-xs',
                  selectedPackageType === type.value && 'shadow-md',
                )}
              >
                {type.label}
              </PackageFilterButton>
            ))}
          </div>

          {/* Desktop (Button Group) */}
          <div className="mb-4 hidden flex-wrap gap-1.5 lg:flex">
            {packageTypes.map((type) => (
              <PackageFilterButton
                key={type.value}
                aria-pressed={selectedPackageType === type.value}
                variant={
                  selectedPackageType === type.value ? 'default' : 'outline'
                }
                size="sm"
                onClick={() => setSelectedPackageType(type.value)}
                className={cn(
                  styles.typeOption,
                  styles.filterOption,
                  'h-7 px-2 py-1 text-xs transition-all',
                  selectedPackageType === type.value && 'shadow-md',
                )}
              >
                {type.label}
              </PackageFilterButton>
            ))}
          </div>
        </>
      )}

      {/* Mobile condensed view */}
      <div className="flex snap-x snap-mandatory items-stretch gap-2 overflow-x-auto py-2 lg:hidden">
        {packages.map((pkg) => {
          const isSelected = selectedPackageId === pkg.id;
          const subscriberInfo = subscriberCounts?.[pkg.id];
          const localized = selectPackageCopy(pkg, i18n.language);

          // Determine package color based on name or use custom color
          let bgColor = pkg.color;
          if (!bgColor) {
            if (pkg.name.toLowerCase().includes('gold')) {
              bgColor = PACKAGE_COLORS.gold;
            } else if (pkg.name.toLowerCase().includes('silver')) {
              bgColor = PACKAGE_COLORS.silver;
            } else if (pkg.name.toLowerCase().includes('bronze')) {
              bgColor = PACKAGE_COLORS.bronze;
            }
          }

          return (
            <Card
              key={pkg.id}
              data-selected={isSelected}
              className={cn(
                styles.optionCard,
                styles.packageCard,
                'relative flex w-[45%] min-w-[150px] flex-shrink-0 cursor-pointer snap-start flex-col overflow-hidden transition-all duration-200',
                isSelected && 'ring-primary shadow-lg ring-2',
                !isSelected && 'hover:shadow-md',
              )}
              onClick={() =>
                onPackageSelect({
                  id: pkg.id,
                  name: pkg.name,
                  price: pkg.price,
                  booking_count: pkg.booking_count,
                  slot_duration: pkg.slot_duration,
                  validity_months: pkg.validity_months,
                  validity_days: pkg.validity_days,
                  original_price: pkg.original_price,
                  package_type: pkg.package_type,
                  player_pricing: pkg.player_pricing,
                  description: localized.description,
                  features: localized.features,
                  color: pkg.color,
                  max_subscribers:
                    subscriberInfo?.max_subscribers ?? pkg.max_subscribers,
                  current_subscribers: subscriberInfo?.current_subscribers,
                  remaining_slots: subscriberInfo?.remaining_slots,
                  is_bundle: pkg.is_bundle,
                  is_fixed_slot: pkg.is_fixed_slot,
                  location_id: pkg.location_id,
                  require_attendance_within_validity:
                    pkg.require_attendance_within_validity,
                  fixed_starts_at: pkg.fixed_starts_at,
                  fixed_ends_at: pkg.fixed_ends_at,
                })
              }
              style={{
                backgroundColor:
                  !presentation.className && bgColor
                    ? `${bgColor}20`
                    : undefined,
              }}
            >
              <CardContent
                className={cn(
                  'flex flex-1 flex-col justify-between space-y-2 p-3 text-center',
                  styles.packageSummary,
                )}
              >
                <h4 className="text-foreground text-sm font-bold">
                  {pkg.name}
                </h4>
                <p className="text-muted-foreground text-xs capitalize">
                  {formatPackageLabel(
                    t,
                    pkg.package_type,
                    pkg.booking_count,
                    pkg.is_bundle,
                  )}
                </p>

                {/* Subscriber limit info */}
                {subscriberInfo && subscriberInfo.max_subscribers !== null && (
                  <Badge
                    variant={
                      subscriberInfo.remaining_slots === 0
                        ? 'destructive'
                        : 'outline'
                    }
                    className="mx-auto min-w-[80px] text-center text-xs whitespace-nowrap"
                  >
                    {subscriberInfo.remaining_slots === 0
                      ? t('soldOut')
                      : t('slotsLeft', {
                          remaining: subscriberInfo.remaining_slots,
                          max: subscriberInfo.max_subscribers,
                        })}
                  </Badge>
                )}

                {/* Price */}
                <div className="space-y-0.5">
                  {pkg.player_pricing && (
                    <p className="text-muted-foreground text-center text-xs">
                      {t('startingAt')}
                    </p>
                  )}
                  <div
                    className={cn(
                      'flex items-baseline justify-center gap-1',
                      styles.packagePrice,
                    )}
                  >
                    <CurrencyIcon className="text-muted-foreground h-3.5 w-3.5" />
                    <span className="text-lg font-bold">
                      {pkg.price.toFixed(0)}
                    </span>
                  </div>
                  <PackageOffersBadge
                    offers={offersForPackage(pkg)}
                    onClick={() => setOffersPackageId(pkg.id)}
                    className="mx-auto mt-1"
                  />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Desktop detailed view */}
      <div className="hidden gap-4 lg:grid">
        {packages.map((pkg) => {
          const isSelected = selectedPackageId === pkg.id;
          const subscriberInfo = subscriberCounts?.[pkg.id];
          const localized = selectPackageCopy(pkg, i18n.language);

          // Determine package color based on name or use custom color
          let bgColor = pkg.color;
          if (!bgColor) {
            if (pkg.name.toLowerCase().includes('gold')) {
              bgColor = PACKAGE_COLORS.gold;
            } else if (pkg.name.toLowerCase().includes('silver')) {
              bgColor = PACKAGE_COLORS.silver;
            } else if (pkg.name.toLowerCase().includes('bronze')) {
              bgColor = PACKAGE_COLORS.bronze;
            }
          }

          return (
            <Card
              key={pkg.id}
              data-selected={isSelected}
              className={cn(
                styles.optionCard,
                styles.packageCard,
                'relative cursor-pointer overflow-hidden transition-all duration-200',
                isSelected && 'ring-primary shadow-lg ring-2',
                !isSelected && 'hover:shadow-md',
              )}
              onClick={() =>
                onPackageSelect({
                  id: pkg.id,
                  name: pkg.name,
                  price: pkg.price,
                  booking_count: pkg.booking_count,
                  slot_duration: pkg.slot_duration,
                  validity_months: pkg.validity_months,
                  validity_days: pkg.validity_days,
                  original_price: pkg.original_price,
                  package_type: pkg.package_type,
                  player_pricing: pkg.player_pricing,
                  description: localized.description,
                  features: localized.features,
                  color: pkg.color,
                  max_subscribers:
                    subscriberInfo?.max_subscribers ?? pkg.max_subscribers,
                  current_subscribers: subscriberInfo?.current_subscribers,
                  remaining_slots: subscriberInfo?.remaining_slots,
                  is_bundle: pkg.is_bundle,
                  is_fixed_slot: pkg.is_fixed_slot,
                  location_id: pkg.location_id,
                  require_attendance_within_validity:
                    pkg.require_attendance_within_validity,
                  fixed_starts_at: pkg.fixed_starts_at,
                  fixed_ends_at: pkg.fixed_ends_at,
                })
              }
            >
              {bgColor && !presentation.className && (
                <div
                  className="absolute inset-0 opacity-10"
                  style={{ backgroundColor: bgColor }}
                />
              )}

              <CardHeader className="relative pb-3">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <h4 className="text-lg font-semibold">{pkg.name}</h4>
                    <p className="text-muted-foreground mt-1 text-sm capitalize">
                      {formatPackageLabel(
                        t,
                        pkg.package_type,
                        pkg.booking_count,
                        pkg.is_bundle,
                      )}
                    </p>
                    {subscriberInfo &&
                      subscriberInfo.max_subscribers !== null && (
                        <Badge
                          variant={
                            subscriberInfo.remaining_slots === 0
                              ? 'destructive'
                              : 'outline'
                          }
                          className="mt-2 whitespace-nowrap"
                        >
                          {subscriberInfo.remaining_slots === 0
                            ? t('soldOut')
                            : t('slotsRemaining', {
                                remaining: subscriberInfo.remaining_slots,
                                max: subscriberInfo.max_subscribers,
                              })}
                        </Badge>
                      )}
                  </div>
                  {isSelected && (
                    <Badge variant="default" className="ms-2 shrink-0">
                      {t('selected')}
                    </Badge>
                  )}
                </div>
              </CardHeader>

              <CardContent className="relative space-y-4">
                {/* Price */}
                <div className="space-y-1">
                  {pkg.player_pricing && (
                    <p className="text-muted-foreground text-sm">
                      {t('startingAt')}
                    </p>
                  )}
                  <div className="flex items-baseline gap-2">
                    <CurrencyIcon className="text-muted-foreground h-5 w-5" />
                    <span className="text-3xl font-bold">
                      {pkg.price.toFixed(0)}
                    </span>
                  </div>
                  <PackageOffersBadge
                    offers={offersForPackage(pkg)}
                    onClick={() => setOffersPackageId(pkg.id)}
                    className="mt-1"
                  />
                </div>

                {/* Validity — fixed-window packages show the actual date range
                    (credits only work inside it); others show the duration. */}
                <div className="text-muted-foreground flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4" />
                  <span>
                    {pkg.fixed_starts_at && pkg.fixed_ends_at
                      ? t('packageValidityWindow', {
                          defaultValue: 'Valid {{start}} – {{end}}',
                          start: formatLocaleDate(
                            pkg.fixed_starts_at,
                            i18n.language,
                          ),
                          end: formatLocaleDate(
                            pkg.fixed_ends_at,
                            i18n.language,
                          ),
                        })
                      : pkg.validity_days
                        ? pkg.validity_days % 30 === 0
                          ? t('expiresIn', { count: pkg.validity_days / 30 })
                          : pkg.validity_days % 7 === 0
                            ? t('expiresInWeeks', {
                                count: pkg.validity_days / 7,
                              })
                            : t('expiresInDays', { count: pkg.validity_days })
                        : t('expiresIn', { count: pkg.validity_months })}
                  </span>
                </div>

                {/* Description (clamped) */}
                {localized.description && (
                  <p
                    className="text-muted-foreground line-clamp-3 text-sm"
                    dir="auto"
                  >
                    {localized.description}
                  </p>
                )}

                {/* Features (capped) */}
                {localized.features.length > 0 && (
                  <ul className="space-y-1">
                    {localized.features
                      .slice(0, PACKAGE_FEATURES_PREVIEW_COUNT)
                      .map((feature, index) => (
                        <li
                          key={index}
                          dir="auto"
                          className="flex items-start text-sm"
                        >
                          <span className="me-2 text-green-500">✓</span>
                          <span className="line-clamp-2">{feature}</span>
                        </li>
                      ))}
                  </ul>
                )}

                {/* View details trigger — shown if description likely overflows
                    or there are more features than the preview cap. */}
                {(localized.features.length > PACKAGE_FEATURES_PREVIEW_COUNT ||
                  (localized.description &&
                    localized.description.length >
                      PACKAGE_DESCRIPTION_OVERFLOW_CHARS)) && (
                  <button
                    type="button"
                    className="text-primary self-start text-xs font-medium underline-offset-2 hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDetailsPackageId(pkg.id);
                    }}
                  >
                    {t('viewDetails')}
                  </button>
                )}

                <Button
                  className={`mt-4 w-full`}
                  variant={isSelected ? 'default' : 'outline'}
                  size="sm"
                >
                  {isSelected ? t('selected') : t('selectPackage')}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Shared "view details" drawer — one mount, opens to whichever
          package's link was tapped. */}
      <DetailsDrawer
        contentClassName={presentation.className}
        contentStyle={presentation.style}
        open={detailsPackageId !== null}
        onOpenChange={(next) => {
          if (!next) setDetailsPackageId(null);
        }}
        {...(() => {
          const pkg = packages.find((p) => p.id === detailsPackageId);
          if (!pkg) return { name: '', description: null, features: [] };
          const copy = selectPackageCopy(pkg, i18n.language);
          return {
            name: pkg.name,
            description: copy.description,
            features: copy.features,
          };
        })()}
      />

      {/* Shared offers drawer — one mount, opens to whichever package's
          offers badge was tapped. */}
      <PackageOffersDrawer
        open={offersPackageId !== null}
        onOpenChange={(next) => {
          if (!next) setOffersPackageId(null);
        }}
        packageName={packages.find((p) => p.id === offersPackageId)?.name ?? ''}
        offers={(() => {
          const pkg = packages.find((p) => p.id === offersPackageId);
          return pkg ? offersForPackage(pkg) : [];
        })()}
      />
    </div>
  );
}
